import DatePicker from '@app/src/components/Form/DatePicker'
import type { Option } from '@app/src/components/Form/Select/ControlledSelect'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import { InfoOutlined } from '@mui/icons-material'
import { Alert, Box, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { isFutureDate } from '../expectationAutomationLogic'
import type { DeadlineType, ExpectationAutomationFormData } from '../types'
import { ExpectationAutomationFormField } from '../types'

const YEAR_OFFSET_OPTIONS: { value: 1 | 2 | 3; labelId: string }[] = [
  { value: 1, labelId: 'form.createExpectationAutomation.deadline.year1Option' },
  { value: 2, labelId: 'form.createExpectationAutomation.deadline.year2Option' },
  { value: 3, labelId: 'form.createExpectationAutomation.deadline.year3Option' },
]

const SetDeadlineStep: React.FC = () => {
  const { formatMessage } = useIntl()
  const { control, watch, setValue, formState, trigger } = useFormContext<ExpectationAutomationFormData>()
  const deadlineType = watch(ExpectationAutomationFormField.DeadlineType)
  const customDateError = formState.errors[ExpectationAutomationFormField.CustomDueDate]

  useEffect(() => {
    if (deadlineType !== 'custom') {
      setValue(ExpectationAutomationFormField.CustomDueDate, null)
    }
  }, [deadlineType, setValue])

  const options: Option<DeadlineType>[] = [
    ...YEAR_OFFSET_OPTIONS.map(({ value, labelId }) => ({
      value,
      label: formatMessage({ id: labelId }),
    })),
    {
      value: 'custom',
      label: formatMessage({ id: 'form.createExpectationAutomation.deadline.customDateOption' }),
    },
  ]

  return (
    <>
      <Select<DeadlineType>
        name={ExpectationAutomationFormField.DeadlineType}
        control={control}
        fieldLabel={formatMessage({ id: 'form.createAutomation.deadlineOffset.dueDate' })}
        options={options}
        required
      />
      {deadlineType === 'custom' && (
        <Box mt={2}>
          <Typography variant="body2">{formatMessage({ id: 'reporting.deadline' })}</Typography>
          <Controller
            name={ExpectationAutomationFormField.CustomDueDate}
            control={control}
            rules={{
              validate: value =>
                isFutureDate(value as string | null | undefined) ||
                formatMessage({ id: 'form.createExpectationAutomation.deadline.customDatePastError' }),
            }}
            render={({ onChange, value }) => (
              <DatePicker
                disablePast
                label=""
                value={(value as string | null | undefined) ?? null}
                onChange={newValue => {
                  onChange(newValue)
                  // Trigger validation after the value changes
                  setTimeout(() => trigger(ExpectationAutomationFormField.CustomDueDate), 0)
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: Boolean(customDateError),
                    helperText: (customDateError?.message as string) ?? undefined,
                  },
                }}
              />
            )}
          />
          <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2 }}>
            {formatMessage({ id: 'form.createAutomation.deadlineOffset.customDateWarning' })}
          </Alert>
        </Box>
      )}
    </>
  )
}

export default SetDeadlineStep
